<template>
  <teleport to="body">
    <div 
      v-if="notifications.length > 0" 
      class="notification-container"
      aria-live="polite"
      aria-label="Notification center"
    >
      <transition-group 
        name="notification" 
        tag="div" 
        class="notifications-wrapper"
      >
        <div
          v-for="notification in notifications"
          :key="notification.id"
          :class="[
            'notification',
            `notification--${notification.type}`
          ]"
          role="alert"
          :aria-live="notification.type === 'error' ? 'assertive' : 'polite'"
        >
          <div class="notification__icon">
            <!-- Success icon -->
            <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            
            <!-- Error icon -->
            <svg v-else-if="notification.type === 'error'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            
            <!-- Warning icon -->
            <svg v-else-if="notification.type === 'warning'" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            
            <!-- Info icon (default) -->
            <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </div>
          
          <div class="notification__content">
            <p class="notification__message">{{ notification.message }}</p>
          </div>
          
          <button
            @click="removeNotification(notification.id)"
            class="notification__close"
            type="button"
            aria-label="Close notification"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { useNotifications } from '../services/notifications.service';

const { notifications, removeNotification } = useNotifications();
</script>

<style scoped>
.notification-container {
  position: fixed;
  top: 3.5rem;
  right: 1rem;
  z-index: 1000;
  max-width: 24rem;
}

.notifications-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.notification {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  background: white;
  border-left: 4px solid;
  max-width: 100%;
  word-wrap: break-word;
}

.notification--success {
  border-left-color: var(--success-color, #10b981);
  background-color: var(--success-color-light, #f0fdf4);
}

.notification--success .notification__icon {
  color: var(--success-color, #10b981);
}

.notification--error {
  border-left-color: var(--error-color, #ef4444);
  background-color: var(--error-color-light, #fef2f2);
}

.notification--error .notification__icon {
  color: var(--error-color, #ef4444);
}

.notification--warning {
  border-left-color: var(--warning-color, #f59e0b);
  background-color: var(--warning-color-light, #fffbeb);
}

.notification--warning .notification__icon {
  color: var(--warning-color, #f59e0b);
}

.notification--info {
  border-left-color: var(--info-color, #3b82f6);
  background-color: var(--info-color-light, #eff6ff);
}

.notification--info .notification__icon {
  color: var(--info-color, #3b82f6);
}

.notification__icon {
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.notification__content {
  flex: 1;
  min-width: 0;
}

.notification__message {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--text-color, #374151);
}

.notification__close {
  flex-shrink: 0;
  background: none;
  border: none;
  color: var(--text-color-light, #6b7280);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

.notification__close:hover {
  color: var(--text-color, #374151);
  background-color: rgba(0, 0, 0, 0.05);
}

.notification__close:focus {
  outline: none;
}

/* Transition animations */
.notification-enter-active {
  transition: all 0.3s ease-out;
}

.notification-leave-active {
  transition: all 0.2s ease-in;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.notification-move {
  transition: transform 0.3s ease;
}

/* Mobile responsiveness */
@media (max-width: 640px) {
  .notification-container {
    top: 0.5rem;
    right: 0.5rem;
    left: 0.5rem;
    max-width: none;
  }
  
  .notification {
    padding: 0.75rem;
  }
  
  .notification__message {
    font-size: 0.8125rem;
  }
}
</style>
